import { deleteCookieAtAllPaths } from './cookie-path-helper'
import { getCookie } from './get-cookie'

/**
 * Delete every cookie visible to the current page, at every plausible path.
 *
 * The previous implementation only emitted `path=/` deletes, which cannot
 * remove cookies set at a deeper path (RFC 6265 §5.4 longest-path-match).
 * Combined with legacy callsites that wrote raw `document.cookie = ...`
 * without specifying a path, this left silent shadowing residues at the
 * URL the user was on when they last logged out — the exact mechanism
 * behind the post-login `?reason=session-expired` loop. See
 * `cookie-path-helper.ts` for the full background.
 *
 * Strategy:
 *   1. Enumerate every cookie name visible from the current URL.
 *   2. For each, walk parent paths of `window.location.pathname` AND of
 *      the `targetUrl` cookie (where the user was when bounced to login),
 *      issuing a delete at each. The `targetUrl` walk is the one that
 *      reaches residues invisible from `/login`.
 *
 * Note: this still cannot delete cookies at paths neither URL covers
 * (e.g. a residue at `/foo` when current URL is `/login` and targetUrl is
 * `/sales`). The auth-aware `hardResetSession` layered on top widens
 * coverage further for known credential-shaped names.
 */
export function deleteAllCookies(): void {
  if (typeof document === 'undefined') return

  // Read targetUrl ONCE, before any deletes — if `targetUrl` is itself
  // among the cookies we're about to wipe, reading it after would fail.
  const targetUrl = getCookie('targetUrl') ?? ''
  const extraPaths = targetUrl ? [targetUrl] : []

  const seen = new Set<string>()
  for (const entry of document.cookie.split(';')) {
    const trimmed = entry.trim()
    if (!trimmed) continue
    const name = trimmed.split('=')[0]?.trim()
    if (!name || seen.has(name)) continue
    seen.add(name)
    deleteCookieAtAllPaths(name, extraPaths)
  }
}
